import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { patch, post } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Avatar } from "@/components/common/Avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/common/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock, Sun, Moon } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export default function Profile() {
  const { me, theme, toggleTheme } = useApp();
  const qc = useQueryClient();
  const [name, setName] = useState(me?.name || "");
  const [skills, setSkills] = useState(me?.skills || []);
  const [skillInput, setSkillInput] = useState("");
  useEffect(() => { setName(me?.name || ""); setSkills(me?.skills || []); }, [me]);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwdError, setPwdError] = useState("");

  const save = useMutation({
    mutationFn: (body) => patch(`/users/${me.id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["me"] }); toast.success("Profile updated"); },
  });

  const changePassword = useMutation({
    mutationFn: (body) => post("/auth/change-password", body),
    onSuccess: () => {
      toast.success("Password changed");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); setPwdError("");
    },
    onError: (err) => setPwdError(err?.response?.data?.detail || "Could not change password"),
  });

  const submitPasswordChange = () => {
    setPwdError("");
    if (!currentPassword || !newPassword) { setPwdError("All fields are required"); return; }
    if (newPassword.length < 8) { setPwdError("New password must be at least 8 characters"); return; }
    if (newPassword !== confirmPassword) { setPwdError("Passwords do not match"); return; }
    changePassword.mutate({ current_password: currentPassword, new_password: newPassword });
  };

  return (
    <div>
      <PageHeader title="My Profile" subtitle="Manage your account, appearance and security." />
      <Tabs defaultValue="profile" className="max-w-2xl">
        <TabsList data-testid="profile-tabs">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="theme" data-testid="tab-theme">Theme</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4 rounded-xl border border-border bg-card p-6">
          <div className="mb-6 flex items-center gap-4">
            <Avatar user={me} size="lg" />
            <div>
              <div className="font-display text-lg font-semibold">{me?.name}</div>
              <div className="text-sm text-muted-foreground">{me?.email}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Full Name</Label>
              <Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={me?.email || ""} disabled className="mt-1" />
            </div>
            {[["Department", me?.department?.name], ["Designation", me?.designation], ["Role", me?.role?.name]].map(([l, v]) => (
              <div key={l}>
                <Label className="flex items-center gap-1"><Lock className="h-3 w-3" />{l}</Label>
                <Input value={v || "—"} disabled className="mt-1 bg-muted" />
              </div>
            ))}
            <div className="col-span-2">
              <Label>Skills</Label>
              <div className="mt-1 flex flex-wrap gap-1 rounded-md border border-input px-2 py-1.5">
                {skills.map((s) => (
                  <span key={s} className="flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs">
                    {s}
                    <button onClick={() => setSkills(skills.filter((x) => x !== s))} className="text-muted-foreground hover:text-foreground">×</button>
                  </span>
                ))}
                <input data-testid="skill-input" className="min-w-[100px] flex-1 bg-transparent text-sm outline-none" placeholder="Type a skill and press Enter"
                  value={skillInput} onChange={(e) => setSkillInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && skillInput.trim()) { setSkills([...skills, skillInput.trim()]); setSkillInput(""); e.preventDefault(); } }} />
              </div>
            </div>
          </div>
          <Button data-testid="save-profile" className="mt-6" onClick={() => save.mutate({ name, skills })}>Save Changes</Button>
        </TabsContent>

        <TabsContent value="theme" className="mt-4 rounded-xl border border-border bg-card p-6">
          <Label>Appearance</Label>
          <div className="mt-3 flex gap-3">
            {[["light", Sun], ["dark", Moon]].map(([t, Icon]) => (
              <button key={t} data-testid={`theme-option-${t}`}
                onClick={() => { if (theme !== t) toggleTheme(); }}
                className={cn("flex flex-1 items-center gap-3 rounded-xl border-2 p-4 capitalize transition-colors",
                  theme === t ? "border-primary bg-primary/5" : "border-border hover:bg-muted")}>
                <Icon className="h-5 w-5" /> {t} mode
              </button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="security" className="mt-4 rounded-xl border border-border bg-card p-6">
          <div className="space-y-4">
            <div>
              <Label>Current Password</Label>
              <PasswordInput autoComplete="current-password" className="mt-1" data-testid="sec-current-password"
                value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            </div>
            <div>
              <Label>New Password</Label>
              <PasswordInput autoComplete="new-password" className="mt-1" data-testid="sec-new-password"
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <PasswordInput autoComplete="new-password" className="mt-1" data-testid="sec-confirm-new-password"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            </div>
            {pwdError && <p className="text-sm text-red-600 dark:text-red-400">{pwdError}</p>}
            <Button data-testid="change-password" onClick={submitPasswordChange} disabled={changePassword.isPending}>
              {changePassword.isPending ? "Changing..." : "Change Password"}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
